import { useCallback } from "react";
import * as XLSX from "xlsx";
import {
  BiddingItemStatusConst,
  ListingCategoryConst,
  ProductionHistoryConst,
} from "../constants";
import type { ExcelInfoItem, ExcelSaleInfoItem } from "../types";
import { ExcelItemsConst, ExcelItemsExpectedHeaders } from "../constants/ExcelItemsConst";
import { ExcelItemsSaleConst, ExcelItemsSaleExpectedHeaders } from "../constants/ExcelItemsSaleConst";
import { useApiClient } from "./useApiClient";
import { TEA_CATEGORY } from "../constants/TeaCategoryConst";

// Excelデータの1行分の型（文字列の配列）
type ExcelRow = string[];
// Excelデータ全体の型（行の配列）
type ExcelData = ExcelRow[];

/**
 * アップロードされたExcelの1行目（見出し）が、選択中の出品区分に対応するデータ列見出しと
 * 一致しているかを判定する（総量等の表示列・それ以降の余剰セルは比較対象に含めない・D-0120）。
 */
const isMatchingExcelHeader = (headerRow: string[], expected: readonly string[]): boolean => {
  for (let i = 0; i < expected.length; i++) {
    if ((headerRow[i] ?? "").trim() !== expected[i]) return false;
  }
  return true;
};

// 結果格納
export interface convertExcelResult {
  data: ExcelInfoItem[];
  message: string;
  result: boolean;
}

// 結果格納（入札会専用）
export interface convertExcelSaleResult {
  data: ExcelSaleInfoItem[];
  message: string;
  result: boolean;
}

export const useExcelQuery = () => {
  const api = useApiClient();

  const DownloadExcel = useCallback(
    async (fileName: string = "template.xlsx") => {
      try {
        // ※毎回新しいAPIとして認識させることで、古いExcelを取得しないようにする
        const response = await api.get(`/Excel/download`, {
          responseType: "blob",
          headers: {
            'Cache-Control': 'no-cache',  // 新しいリクエストを強制する（HTTP/1.1用）
            'Pragma': 'no-cache',  // 古いブラウザやプロキシ用（HTTP/1.0用）
            'Expires': '0',  // すぐに有効期限が切れるように設定
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        });

        // ガード処理: 万が一エラーメッセージ(JSON)が返ってきている場合はダウンロードさせない
        if (response.data.type === "application/json") {
          const textData = await response.data.text();
          const errorData = JSON.parse(textData);

          throw new Error(errorData.message || "サーバーエラーが発生しました");
        }

        // Blobの生成（念の為、型指定を明示）
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // ダウンロード実行
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        // クリーンアップ
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (ex) {
        console.error("Excelのダウンロードに失敗しました。", ex);
        throw ex;
      }
    },
    [api],
  );

  /**
   * 入札会専用テンプレートExcelのダウンロード処理（DownloadExcelの姉妹メソッド）
   */
  const DownloadSaleExcel = useCallback(
    async (fileName: string = "template.xlsx") => {
      try {
        const response = await api.get(`/Excel/download-sale`, {
          responseType: "blob",
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        });

        if (response.data.type === "application/json") {
          const textData = await response.data.text();
          const errorData = JSON.parse(textData);

          throw new Error(errorData.message || "サーバーエラーが発生しました");
        }

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();

        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (ex) {
        console.error("入札会専用ひな形のダウンロードに失敗しました。", ex);
        throw ex;
      }
    },
    [api],
  );

  /**
   * 結果Excelのダウンロード処理
   * @param blob 
   * @param fileName 
   */
  const DownloadResultExcel = (base64: string, fileName: string) => {
    // Base64をデコードしてバイナリ文字列にする
    const bin = atob(base64);
    
    // バイナリ文字列をUint8Array（バイト配列）に変換
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    // Blobオブジェクトを作成 (ExcelのMIMEタイプ指定)
    const blob = new Blob([buffer.buffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
    });

    // ダウンロードリンクを生成してクリック
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link); // 後始末
  };

  /**
   * Excelファイルデータ変換
   * @param file
   * @returns
   */
  const ConvertExcel = async (file: File, biddingDate: number | undefined): Promise<convertExcelResult> =>{
    let result: convertExcelResult = {
      data: [],
      message: "",
      result: false,
    };

    let excelData: ExcelData | null = [];

    //#region Excelファイルデータ抽出
    if (!file) {
      result.data = [];
      result.message = "ファイルデータが存在していません。";
      result.result = false;
      return result;
    }

    // MIMEタイプまたは拡張子の簡易チェック
    const validExtensions = [".xlsx", ".xls"];
    const isExcel = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext),
    );

    if (!isExcel) {
      // setError('サポートされていないファイル形式です。.xlsx, .xlsのみ対応しています。');
      result.data = [];
      result.message = "サポートされていないファイル形式です。.xlsx, .xlsのみ対応しています。";
      result.result = false;
      return result;
    }

    try {
      // ファイルの読み込み処理
      const data = await file.arrayBuffer();

      // Workbookの読み込み
      const workbook = XLSX.read(data, { type: "array" });

      // 最初のシート名を取得
      const sheetName = workbook.SheetNames[0];

      // シートが存在しない場合のエラーハンドリング
      if (!sheetName) {
        // throw new Error("Excelファイル内にシートが見つかりません。");
        result.data = [];
        result.message = "Excelファイル内にシートが見つかりません。";
        result.result = false;
        return result;
      }

      const worksheet = workbook.Sheets[sheetName];

      // シートデータを2次元配列（string[][]）に変換
      // header: 1 を指定することで、キーバリュー形式ではなく配列の配列として取得
      // defval: "" を指定することで、空セルを空文字として扱う
      const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: "",
        raw: false, // すべての値を文字列としてフォーマットして取得
      }) as string[][];

      // 1行目はヘッダー、2-3行目はスキップ、4行目以降をデータとして結合
      if (jsonData.length > 0) {
        const headerRow = jsonData[0]; // 1行目 (Index 0)

        // 選択中の出品区分（通常入札）と一致するファイルかどうかを見出しで判定する（D-0120）。
        if (!isMatchingExcelHeader(headerRow, ExcelItemsExpectedHeaders)) {
          result.data = [];
          result.message = "通常入札用Excelではありません。通常入札用Excelをダウンロードして使用してください。";
          result.result = false;
          return result;
        }

        // 2行目(Index 1)、3行目(Index 2) はスキップ
        // 4行目 (Index 3) 以降
        const dataRows = jsonData.slice(3).filter((row) => {
          // 中身が全てnullもしくは空の場合は除外
          const hasValue = (row as string[]).some((cell) => {
            return cell !== null && cell !== undefined && String(cell).trim() !== '';
          });
          return hasValue;
        });

        excelData = [headerRow, ...dataRows];
      } else {
        excelData = [];
      }
    } catch (err) {
      console.error(err);
      // setError('ファイルの解析中にエラーが発生しました。');
      result.data = [];
      result.message = "ファイルの解析中にエラーが発生しました。";
      result.result = false;
      return result;
    }
    //#endregion

    //#region ExcelデータをBiddingInfoItemに整形
    let today = new Date().toLocaleDateString("sv-SE").replace(/-/g, ""); // 本日日付

    // データがヘッダー行しかない、または空の場合は空配列を返す
    if (!excelData || excelData.length < 2) {
      result.data = [];
      result.message = "データが入力されていません。";
      result.result = false;
      return result;
    }

    result.data = excelData.slice(1)
                    .map((row) => {

                      return {
                        itemId: "",
                        receptionNumber: "",
                        receptionDate: today || "", //受付日(当日)
                        biddingDate: String(biddingDate) || "", //入札日(直近の開催日)
                        subItems: [], 
                        
                        //Excel側の並び順
                        sellerCode: row[ExcelItemsConst.SellerCode] || "", sellerName: "", 
                        farmerCode: row[ExcelItemsConst.FarmerCode] || "", farmerName: row[ExcelItemsConst.FarmerName], 
                        isFarmerHidden: row[ExcelItemsConst.IsFarmerHidden] || "",
                        teaSeasonCode: row[ExcelItemsConst.TeaSeasonCode] || "",  //茶期
                        teaTypeCode: row[ExcelItemsConst.TeaTypeCode] || "",  //茶種
                        brand: row[ExcelItemsConst.Brand] || "",  //荷印
                        varietyCode: row[ExcelItemsConst.VarietyCode] || "", //品種コード)
                        varietyName: row[ExcelItemsConst.VarieryNameFree] || "", //品種名
                        gapCode: row[ExcelItemsConst.GapCode] || "",
                        caltivationCategory:  row[ExcelItemsConst.CaltivationCategoryCode] || "", //栽培区分
                        capacity1: row[ExcelItemsConst.Capacity1] || "",  //容量①
                        quantity1: row[ExcelItemsConst.Quantity1] || "",  //本数①
                        capacity2: row[ExcelItemsConst.Capacity2] || "",  //容量②
                        quantity2: row[ExcelItemsConst.Quantity2] || "",  //本数②
                        bidMinPrice: row[ExcelItemsConst.BidMinPrice] || "", //最低落札値
                        biko: row[ExcelItemsConst.Biko] || "", //備考

                        //固定値
                        productionHistory: String(ProductionHistoryConst.YES), //生産履歴「有」固定
                        listingCategory: String(ListingCategoryConst.BIDDING), //出品区分「通常入札」固定
                        status: String(BiddingItemStatusConst.INPUT), //ステータス「入力中」固定
                        teaCategory: String(TEA_CATEGORY.ARACHA), //茶区分

                        //Excel上にはあるが、Codeプロパティ側でそのまま渡すため空
                        teaSeasonName: "",
                        teaTypeName: "",
                        gapName: "",
                      };
      });

    result.message = "処理が完了しました。";
    result.result = true;

    return result;
  };

  /**
   * Excelファイルデータ変換（入札会専用・ConvertExcelの姉妹メソッド）。
   * GAPを扱わず、茶区分・生産履歴・製法区分・特記事項・備考1〜3はExcel値をそのまま使う
   * （既存の決め打ちを踏襲しない・design-sale-bulk-upload.md §1継ぎ目K）。
   * @param file
   * @returns
   */
  const ConvertSaleExcel = async (file: File, biddingDate: number | undefined): Promise<convertExcelSaleResult> =>{
    let result: convertExcelSaleResult = {
      data: [],
      message: "",
      result: false,
    };

    let excelData: ExcelData | null = [];

    //#region Excelファイルデータ抽出
    if (!file) {
      result.data = [];
      result.message = "ファイルデータが存在していません。";
      result.result = false;
      return result;
    }

    // MIMEタイプまたは拡張子の簡易チェック
    const validExtensions = [".xlsx", ".xls"];
    const isExcel = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext),
    );

    if (!isExcel) {
      result.data = [];
      result.message = "サポートされていないファイル形式です。.xlsx, .xlsのみ対応しています。";
      result.result = false;
      return result;
    }

    try {
      // ファイルの読み込み処理
      const data = await file.arrayBuffer();

      // Workbookの読み込み
      const workbook = XLSX.read(data, { type: "array" });

      // 最初のシート名を取得
      const sheetName = workbook.SheetNames[0];

      // シートが存在しない場合のエラーハンドリング
      if (!sheetName) {
        result.data = [];
        result.message = "Excelファイル内にシートが見つかりません。";
        result.result = false;
        return result;
      }

      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: "",
        raw: false,
      }) as string[][];

      // 1行目はヘッダー、2-3行目はスキップ、4行目以降をデータとして結合
      if (jsonData.length > 0) {
        const headerRow = jsonData[0];

        // 選択中の出品区分（入札会）と一致するファイルかどうかを見出しで判定する（D-0120）。
        if (!isMatchingExcelHeader(headerRow, ExcelItemsSaleExpectedHeaders)) {
          result.data = [];
          result.message = "入札会専用Excelではありません。入札会専用Excelをダウンロードして使用してください。";
          result.result = false;
          return result;
        }

        const dataRows = jsonData.slice(3).filter((row) => {
          const hasValue = (row as string[]).some((cell) => {
            return cell !== null && cell !== undefined && String(cell).trim() !== '';
          });
          return hasValue;
        });

        excelData = [headerRow, ...dataRows];
      } else {
        excelData = [];
      }
    } catch (err) {
      console.error(err);
      result.data = [];
      result.message = "ファイルの解析中にエラーが発生しました。";
      result.result = false;
      return result;
    }
    //#endregion

    //#region ExcelデータをExcelSaleInfoItemに整形
    let today = new Date().toLocaleDateString("sv-SE").replace(/-/g, ""); // 本日日付

    if (!excelData || excelData.length < 2) {
      result.data = [];
      result.message = "データが入力されていません。";
      result.result = false;
      return result;
    }

    result.data = excelData.slice(1)
                    .map((row) => {

                      return {
                        itemId: "",
                        receptionNumber: "",
                        receptionDate: today || "", //受付日(当日)
                        biddingDate: String(biddingDate) || "", //入札日(直近の開催日)
                        subItems: [],

                        //Excel側の並び順
                        sellerCode: row[ExcelItemsSaleConst.SellerCode] || "", sellerName: "",
                        farmerCode: row[ExcelItemsSaleConst.FarmerCode] || "", farmerName: row[ExcelItemsSaleConst.FarmerName],
                        isFarmerHidden: row[ExcelItemsSaleConst.IsFarmerHidden] || "",
                        teaCategory: row[ExcelItemsSaleConst.TeaCategoryCode] || "", //茶区分
                        teaSeasonCode: row[ExcelItemsSaleConst.TeaSeasonCode] || "",  //茶期
                        teaTypeCode: row[ExcelItemsSaleConst.TeaTypeCode] || "",  //茶種
                        brand: row[ExcelItemsSaleConst.Brand] || "",  //荷印
                        varietyCode: row[ExcelItemsSaleConst.VarietyCode] || "", //品種コード
                        varietyName: row[ExcelItemsSaleConst.VarieryNameFree] || "", //品種名
                        caltivationCategory: row[ExcelItemsSaleConst.CaltivationCategoryCode] || "", //栽培区分
                        productionHistory: row[ExcelItemsSaleConst.ProductionHistory] || "", //生産履歴（決め打ちしない）
                        manufacturingMethod: row[ExcelItemsSaleConst.ManufacturingMethod] || "", //製法区分
                        capacity1: row[ExcelItemsSaleConst.Capacity1] || "",  //容量①
                        quantity1: row[ExcelItemsSaleConst.Quantity1] || "",  //本数①
                        capacity2: row[ExcelItemsSaleConst.Capacity2] || "",  //容量②
                        quantity2: row[ExcelItemsSaleConst.Quantity2] || "",  //本数②
                        bidMinPrice: row[ExcelItemsSaleConst.BidMinPrice] || "", //最低落札値
                        biko: row[ExcelItemsSaleConst.Biko] || "", //特記事項
                        saleBiko1: row[ExcelItemsSaleConst.SaleBiko1] || "", //備考1
                        saleBiko2: row[ExcelItemsSaleConst.SaleBiko2] || "", //備考2
                        saleBiko3: row[ExcelItemsSaleConst.SaleBiko3] || "", //備考3

                        //固定値
                        listingCategory: String(ListingCategoryConst.SALE), //出品区分「入札会」固定
                        status: String(BiddingItemStatusConst.INPUT), //ステータス「入力中」固定

                        //Excel上にはあるが、Codeプロパティ側でそのまま渡すため空
                        teaSeasonName: "",
                        teaTypeName: "",
                      };
      });

    result.message = "処理が完了しました。";
    result.result = true;

    return result;
  };

  return {
    DownloadExcel,
    DownloadSaleExcel,
    DownloadResultExcel,
    ConvertExcel,
    ConvertSaleExcel,
  };
};
